/**
 * Constantes de rutas centralizadas para toda la aplicación
 * 
 * Todas las rutas protegidas están bajo /dashboard para mantener
 * la landing page (/) como ruta pública.
 */

// ============================================================================
// RUTAS PÚBLICAS
// ============================================================================
export const ROUTES = {
  // Públicas - Landing y autenticación
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY_EMAIL: '/verify-email',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  LANDING: '/registro-gratis',
  
  // Públicas - Acceso por QR y trial
  PUBLIC_PET: (id: string | number) => `/pet/${id}`,
  EXTEND_TRIAL: '/subscription/extend-trial',

  // Pública - Página puente de retorno de pago para la app móvil (App Link).
  PAYMENT_APP_RETURN: '/app/payment/result',

  // Públicas - Legales (URL accesibles exigidas por App Store y Google Play).
  PRIVACY: '/privacidad',
  TERMS: '/terminos',

  // ============================================================================
  // RUTAS PROTEGIDAS - DASHBOARD
  // ============================================================================
  
  // Dashboard base
  DASHBOARD: '/dashboard',
  
  // Dashboard - Perfil y configuración
  PROFILE: '/dashboard/profile',
  REFERRALS: '/dashboard/referrals',
  APP_RATING: '/dashboard/app-rating',
  
  // Dashboard - Mascotas
  PETS_NEW: '/dashboard/pets/new',
  PET_DETAIL: (id: string | number) => `/dashboard/pets/${id}`,
  PET_EDIT: (id: string | number) => `/dashboard/pets/${id}/edit`,
  PET_CARNET: (id: string | number) => `/dashboard/pets/${id}/carnet`,
  
  // Dashboard - Suscripciones y pagos
  SUBSCRIPTIONS_PLANS: '/dashboard/subscriptions/plans',
  MY_PAYMENT_HISTORY: '/dashboard/my-payment-history',
  PAYMENT: '/dashboard/payment',
  PAYMENT_SUCCESS: '/dashboard/payment/success',
  PAYMENT_FAILURE: '/dashboard/payment/failure',
  PAYMENT_PENDING: '/dashboard/payment/pending',
  PAYMENT_CANCEL: '/dashboard/payment/cancel',
  
  // Dashboard - Citas y horarios
  APPOINTMENTS: '/dashboard/appointments',
  SCHEDULES: '/dashboard/schedules',
  
  // Dashboard - Gastos
  EXPENSES_PREVIEW: '/dashboard/expenses/preview',
  
  // Dashboard - Veterinario
  VETERINARIAN: '/dashboard/veterinarian',
  
  // Dashboard - Owner
  OWNER_CLINIC_REQUEST: '/dashboard/owner/clinic-request',
  
  // Dashboard - Clínica
  CLINIC_PET_REQUESTS: '/dashboard/clinic/pet-requests',
  
  // Dashboard - Admin
  ADMIN: '/dashboard/admin',
  ADMIN_NOTIFICATIONS: '/dashboard/admin/notifications',
  ADMIN_CLINICS: '/dashboard/admin/clinics',
  ADMIN_CLINIC_DETAIL: (id: string | number) => `/dashboard/admin/clinics/${id}`,
  ADMIN_PET_CLINICS: '/dashboard/admin/pet-clinics',
  ADMIN_PETS: '/dashboard/admin/pets',
  ADMIN_SYSTEM_SETTINGS: '/dashboard/admin/system-settings',
  ADMIN_PLAN_LIMITS: '/dashboard/admin/plan-limits',
  ADMIN_REPORTS: '/dashboard/admin/reports',
  ADMIN_INVENTORY: '/dashboard/admin/inventory',
  ADMIN_USERS: '/dashboard/admin/users',
  ADMIN_USER_PLANS: '/dashboard/admin/user-plans',
  ADMIN_USER_AUDIT: '/dashboard/admin/user-audit',
  ADMIN_EMAILS: '/dashboard/admin/emails',
  ADMIN_MERCADOPAGO: '/dashboard/admin/mercadopago',
  ADMIN_SUGGESTIONS: '/dashboard/admin/suggestions',
  ADMIN_PAYMENT_APPROVALS: '/dashboard/admin/payment-approvals',
  ADMIN_MAINTENANCE: '/dashboard/admin/maintenance',
  ADMIN_VERIPAGO: '/dashboard/admin/veripago',
  ADMIN_PRIVACY_REQUESTS: '/dashboard/admin/privacy-requests',
} as const;

/**
 * Helper para construir rutas dinámicas
 */
export const buildRoute = {
  petDetail: (id: string | number) => ROUTES.PET_DETAIL(id),
  petEdit: (id: string | number) => ROUTES.PET_EDIT(id),
  petCarnet: (id: string | number) => ROUTES.PET_CARNET(id),
  publicPet: (id: string | number) => ROUTES.PUBLIC_PET(id),
  adminClinicDetail: (id: string | number) => ROUTES.ADMIN_CLINIC_DETAIL(id),
};

/**
 * Verificar si una ruta es pública (no requiere autenticación)
 */
export const isPublicRoute = (path: string): boolean => {
  const publicRoutes = [
    ROUTES.HOME,
    ROUTES.LOGIN,
    ROUTES.REGISTER,
    ROUTES.VERIFY_EMAIL,
    ROUTES.FORGOT_PASSWORD,
    ROUTES.RESET_PASSWORD,
    ROUTES.LANDING,
    ROUTES.EXTEND_TRIAL,
  ];
  
  // Verificar rutas exactas
  if (publicRoutes.includes(path as any)) {
    return true;
  }
  
  // Verificar rutas públicas dinámicas
  if (path.startsWith('/pet/')) {
    return true;
  }
  
  return false;
};

/**
 * Verificar si una ruta es del dashboard
 */
export const isDashboardRoute = (path: string): boolean => {
  return path.startsWith('/dashboard');
};
